/* Maps the LLM `highlight_region` tool's ISO 3166 alpha-2/alpha-3 codes onto
   the region codes the backend deep-dive endpoint actually supports
   (`/api/dashboard/region/<code>` — see backend REGION_NAMES).

   Codes we can't confidently map resolve to null; the caller shows a
   graceful "not supported" toast instead of an opaque 404. */

/** Region codes the backend region deep-dive supports natively. */
export const SUPPORTED_REGION_CODES = new Set([
  "MENA",
  "ASIA",
  "PAC",
  "ATL",
  "AM",
  "RED_SEA",
  "SUEZ",
  "US",
  "EU",
  "HORMUZ",
  "MALACCA",
  "PANAMA",
  "GLOBAL",
])

/* ISO alpha-3 → alpha-2 for the countries we can place in a region group. */
const ALPHA3_TO_ALPHA2: Record<string, string> = {
  USA: "US", CAN: "CA", MEX: "MX", BRA: "BR", ARG: "AR", CHL: "CL", PER: "PE",
  COL: "CO", PAN: "PA", ECU: "EC", URY: "UY", VEN: "VE", GTM: "GT", CRI: "CR",
  DOM: "DO", BOL: "BO", PRY: "PY",
  NLD: "NL", DEU: "DE", FRA: "FR", BEL: "BE", ITA: "IT", ESP: "ES", PRT: "PT",
  POL: "PL", SWE: "SE", DNK: "DK", FIN: "FI", IRL: "IE", AUT: "AT", GRC: "GR",
  ROU: "RO", BGR: "BG", HRV: "HR", SVN: "SI", SVK: "SK", CZE: "CZ", HUN: "HU",
  LTU: "LT", LVA: "LV", EST: "EE", LUX: "LU", MLT: "MT", CYP: "CY",
  CHN: "CN", JPN: "JP", KOR: "KR", TWN: "TW", HKG: "HK", SGP: "SG", MYS: "MY",
  THA: "TH", VNM: "VN", IDN: "ID", PHL: "PH", IND: "IN", PAK: "PK", BGD: "BD",
  LKA: "LK", KHM: "KH", MMR: "MM",
  SAU: "SA", ARE: "AE", EGY: "EG", QAT: "QA", KWT: "KW", OMN: "OM", BHR: "BH",
  IRQ: "IQ", IRN: "IR", ISR: "IL", JOR: "JO", LBN: "LB", YEM: "YE", SYR: "SY",
  LBY: "LY", DZA: "DZ", MAR: "MA", TUN: "TN",
  AUS: "AU", NZL: "NZ",
}

/* ISO alpha-2 → supported region grouping. Conservative: only countries that
   clearly belong to one of the backend's region buckets are mapped. */
const ISO2_TO_REGION: Record<string, string> = {
  // United States has its own deep-dive code.
  US: "US",
  // European Union members.
  NL: "EU", DE: "EU", FR: "EU", BE: "EU", IT: "EU", ES: "EU", PT: "EU",
  PL: "EU", SE: "EU", DK: "EU", FI: "EU", IE: "EU", AT: "EU", GR: "EU",
  RO: "EU", BG: "EU", HR: "EU", SI: "EU", SK: "EU", CZ: "EU", HU: "EU",
  LT: "EU", LV: "EU", EE: "EU", LU: "EU", MT: "EU", CY: "EU",
  // Asia.
  CN: "ASIA", JP: "ASIA", KR: "ASIA", TW: "ASIA", HK: "ASIA", SG: "ASIA",
  MY: "ASIA", TH: "ASIA", VN: "ASIA", ID: "ASIA", PH: "ASIA", IN: "ASIA",
  PK: "ASIA", BD: "ASIA", LK: "ASIA", KH: "ASIA", MM: "ASIA",
  // Middle East / North Africa.
  SA: "MENA", AE: "MENA", EG: "MENA", QA: "MENA", KW: "MENA", OM: "MENA",
  BH: "MENA", IQ: "MENA", IR: "MENA", IL: "MENA", JO: "MENA", LB: "MENA",
  YE: "MENA", SY: "MENA", LY: "MENA", DZ: "MENA", MA: "MENA", TN: "MENA",
  // Americas.
  CA: "AM", MX: "AM", BR: "AM", AR: "AM", CL: "AM", PE: "AM", CO: "AM",
  PA: "AM", EC: "AM", UY: "AM", VE: "AM", GT: "AM", CR: "AM", DO: "AM",
  BO: "AM", PY: "AM",
  // Pacific.
  AU: "PAC", NZ: "PAC",
}

/**
 * Resolve a highlight_region code to a region the deep-dive supports.
 * Accepts: a supported region code directly (MENA, RED_SEA, …), an ISO
 * alpha-2 country code, or an ISO alpha-3 country code.
 * Returns null when the code can't be mapped.
 */
export function resolveRegionCode(raw: string | undefined | null): string | null {
  if (!raw) return null
  const code = raw.trim().toUpperCase()
  if (!code) return null
  if (SUPPORTED_REGION_CODES.has(code)) return code
  const alpha2 = code.length === 3 ? ALPHA3_TO_ALPHA2[code] ?? null : code
  if (!alpha2) return null
  return ISO2_TO_REGION[alpha2] ?? null
}
